import React, { useState, useMemo, ChangeEvent, useEffect } from "react";
import {
  Td,
  Th,
  InnerTable,
  Alert,
  Tr,
  ScrollWrapper,
  Input,
  Select,
} from "./TransactionUploader.styles";
import { RowState } from "./types";
import {
  VersamentoEnriched,
  Proprietario,
  Inquilino,
  UnitaImmobiliare,
} from "../../services/types/firestoreTypes";

interface Person {
  id: string;
  name: string;
  type: "owner" | "tenant";
}

interface Props {
  index: number;
  rowState: RowState;
  owners: Proprietario[];
  tenants: Inquilino[];
  units: UnitaImmobiliare[];
  onSaveInline: (
    rowIndex: number,
    versamento: VersamentoEnriched,
    versIndex?: number
  ) => void;
  onDeleteInline?: (rowIndex: number, versIndex: number) => void;
}

const parseLocalizedNumber = (value: string): number =>
  Number(value.replace(/\./g, "").replace(/,/g, ".")) || 0;

const TransactionRow: React.FC<Props> = ({
  index,
  rowState,
  owners,
  tenants,
  units,
  onSaveInline,
  onDeleteInline = () => { },
}) => {
  const { tx, analysis, versamenti, error, processing } = rowState;
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [addMode, setAddMode] = useState(false);
  const [lastEdited, setLastEdited] = useState<"ui_id" | "nome" | "person_id" | null>(null);



  const persons: Person[] = useMemo(
    () => [
      ...owners.map((o) => ({
        id: o.cod_condomino,
        name: o.ac_nom,
        type: "owner" as const,
      })),
      ...tenants.map((t) => ({
        id: t.cod_inquilino,
        name: t.iq_nom,
        type: "tenant" as const,
      })),
    ],
    [owners, tenants]
  );

  const initialForm: Partial<VersamentoEnriched> & {
    personType: "owner" | "tenant";
  } = {
    cod_con: tx["CodiceCondominio"],
    gestione: "VO",
    personType: "owner",
  };
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    setFormData((fd) => ({
      ...fd,
      nome: undefined,
      person_id: undefined,
      ui_id: undefined,
    }));
  }, [formData.personType]);

  // const dataContabile = tx["Data Contabile"];
  const isInbound = parseLocalizedNumber(tx["Avere"]) > 0;
  const isOutbound = parseLocalizedNumber(tx["Dare"]) > 0;
  const desc = tx["Descrizione Causale ABI/SWIFT"] || "";
  const isBlocked =
    isOutbound || desc.startsWith("078 - ") || desc.startsWith("022 - ") || desc.startsWith("0Z2 - ") ;

  const processed = versamenti !== undefined;
  const processingAnimation = !processed && processing;

  let bg: string | undefined;
  if (error) {
    bg = "#f8d7da";
  } else if (isInbound) {
    if (!processed && processing) bg = "#cfe2ff";
    else if (!processed && !processing) bg = "#f0f0f0";
    // else bg = versamenti!.length === 0 ? '#f8d7da' : versamenti!.length === 1 ? '#d4edda' : '#fff3cd';
    else bg = versamenti!.length === 0 ? "#f8d7da" : "#d4edda";
  }

  const colCount = Object.keys(tx).length;

  const filteredUnits = useMemo(
    () => units.filter((u) => u.cod_ui.includes(formData.ui_id || "")),
    [units, formData.ui_id]
  );

  const filteredPersons = useMemo(
    () =>
      persons.filter(
        (p) =>
          p.type === formData.personType &&
          (p.name.toLowerCase().includes((formData.nome || "").toLowerCase()) ||
            p.id.includes(formData.person_id || ""))
      ),
    [persons, formData.personType, formData.nome, formData.person_id]
  );


  // Whenever user types into one field, we’ll sync the others in a single effect:
  useEffect(() => {
    if (!lastEdited) return;

    let updates: Partial<typeof formData> = {};
    if (lastEdited === "nome") {
      const p = persons.find(
        (p) => p.name === formData.nome && p.type === formData.personType
      );
      if (p) {
        const uiMatch = units.find((u) =>
          p.type === "owner" ? u.cod_condomino === p.id : u.cod_inquilino === p.id
        )?.cod_ui;
        if (formData.person_id !== p.id) updates.person_id = p.id;
        if (formData.ui_id !== uiMatch) updates.ui_id = uiMatch;
      }
    } else if (lastEdited === "person_id") {
      const p = persons.find(
        (p) => p.id === formData.person_id && p.type === formData.personType
      );
      if (p) {
        if (formData.nome !== p.name) updates.nome = p.name;
        const uiMatch = units.find((u) =>
          p.type === "owner" ? u.cod_condomino === p.id : u.cod_inquilino === p.id
        )?.cod_ui;
        if (formData.ui_id !== uiMatch) updates.ui_id = uiMatch;
      }
    } else if (lastEdited === "ui_id") {
      const u = units.find((u) => u.cod_ui === formData.ui_id);
      if (u) {
        let newPersonId: string | undefined;
        let newName: string | undefined;
        if (formData.personType === "owner" && u.cod_condomino) {
          newPersonId = u.cod_condomino;
          newName = owners.find(o => o.cod_condomino === u.cod_condomino)?.ac_nom;
        }
        if (formData.personType === "tenant" && u.cod_inquilino) {
          newPersonId = u.cod_inquilino;
          newName = tenants.find(t => t.cod_inquilino === u.cod_inquilino)?.iq_nom;
        }
        if (formData.person_id !== newPersonId) updates.person_id = newPersonId;
        if (formData.nome !== newName) updates.nome = newName;
      }
    }

    // Only write state if there’s something to change
    if (Object.keys(updates).length > 0) {
      setFormData(fd => ({ ...fd, ...updates }));
    }
  }, [
    lastEdited,
    formData.nome,
    formData.person_id,
    formData.ui_id,
    formData.personType,
    persons,
    owners,
    tenants,
    units,
  ]);

  // Reset form when switching out of edit/add
  const cancel = () => {
    setEditIdx(null);
    setAddMode(false);
    setFormData(initialForm);
    setLastEdited(null);
  };

  // ← make sure this lives *after* your lastEdited useState
  const handleInput = (field: keyof typeof formData) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.value as any;
      setFormData(fd => ({ ...fd, [field]: value }));

      if (field === "ui_id" || field === "nome" || field === "person_id") {
        setLastEdited(field); // solo per questi tre
      }

      if (field === "personType") {
        const u = units.find(u => u.cod_ui === (formData.ui_id ?? ""));
        if (u) {
          if (value === "owner") {
            const newId = u.cod_condomino;
            setFormData(fd => ({
              ...fd,
              person_id: newId,
              nome: owners.find(o => o.cod_condomino === newId)?.ac_nom
            }));
          } else {
            const newId = u.cod_inquilino;
            setFormData(fd => ({
              ...fd,
              person_id: newId,
              nome: tenants.find(t => t.cod_inquilino === newId)?.iq_nom
            }));
          }
        } else {
          // se non c’è ui selezionata, almeno pulisci coerentemente
          setFormData(fd => ({ ...fd, person_id: undefined, nome: undefined }));
        }
      }
    };


  const startEdit = (v: VersamentoEnriched, vi: number) => {
    const type: "owner" | "tenant" =
      v.person_role ??
      (owners.some(o => o.cod_condomino === v.person_id) ? "owner" : "tenant"); // fallback
    setFormData({ ...v, cod_con: tx["CodiceCondominio"], personType: type });
    setEditIdx(vi);
    setAddMode(false);
  };


  const startAdd = () => {
    setFormData({ ...initialForm });
    setAddMode(true);
    setEditIdx(null);
  };

  const handleSave = (vi?: number) => {
    let payload = { ...formData };

    // Se manca person_id ma c’è ui_id, deriviamolo dal Tipo
    if (!payload.person_id && payload.ui_id) {
      const u = units.find(u => u.cod_ui === payload.ui_id);
      if (u) {
        if (payload.personType === "owner" && u.cod_condomino) {
          payload.person_id = u.cod_condomino;
          payload.nome = owners.find(o => o.cod_condomino === u.cod_condomino)?.ac_nom;
        }
        if (payload.personType === "tenant" && u.cod_inquilino) {
          payload.person_id = u.cod_inquilino;
          payload.nome = tenants.find(t => t.cod_inquilino === u.cod_inquilino)?.iq_nom;
        }
      }
    }

    // Validazioni minime
    if (!payload.person_id || !payload.ui_id) {
      // mostrare un alert/toast, bloccare il salvataggio
      return;
    }

    onSaveInline(
      index,
      {
        ...payload,
        person_role: formData.personType,      // <<< salva il ruolo
        imp_tot: Number(payload.imp_tot),
        num_prev: Number(payload.num_prev),
      } as VersamentoEnriched,
      vi
    );
    cancel();
  };

  const handleDelete = (vi: number) => {
    onDeleteInline(index, vi);
  };
  const getPersonDisplayType = (id?: string) =>
    owners.some(o => o.cod_condomino === id) ? "Proprietario" :
      tenants.some(t => t.cod_inquilino === id) ? "Inquilino" : "—";


  return (
    <>
      <Tr
        disabled={isBlocked}
        isAnimating={processingAnimation && !error}
        style={
          isBlocked
            ? { opacity: 0.4, pointerEvents: "none" }
            : bg
              ? { backgroundColor: bg }
              : undefined
        }
      >
        {Object.values(tx).map((v, i) => (
          <Td key={i}>{v}</Td>
        ))}
      </Tr>

      {error && !analysis && (
        <Tr>
          <Td colSpan={colCount}>
            <Alert>{error}</Alert>
          </Td>
        </Tr>
      )}

      {analysis && (
        <Tr>
          <Td colSpan={colCount} style={{ paddingLeft: "100px" }}>
            <ScrollWrapper>
              <InnerTable isAnimating={processingAnimation && !error}>
                <thead>
                  <tr>
                    <Th>Unità Imm.</Th>
                    <Th>Tipo</Th>
                    <Th>Nome</Th>
                    <Th>Id</Th>
                    <Th>Gestione</Th>
                    <Th>Preventivo</Th>
                    <Th>Importo</Th>
                    <Th>Azioni</Th>
                  </tr>
                </thead>
                <tbody
                  style={
                    error
                      ? { backgroundColor: "#f8d7da" }
                      : bg
                        ? { backgroundColor: bg }
                        : undefined
                  }
                >
                  {versamenti?.map((v, vi) =>
                    editIdx === vi ? (
                      <tr key={vi}>
                        <Td>
                          <Input
                            list={`ui-list-${index}-${vi}`}
                            value={formData.ui_id || ""}
                            onChange={handleInput("ui_id")}
                          />
                          <datalist id={`ui-list-${index}-${vi}`}>
                            {filteredUnits.map((u) => (
                              <option key={u.cod_ui} value={u.cod_ui}>
                                {u.des_ui}
                              </option>
                            ))}
                          </datalist>
                        </Td>
                        <Td>
                          <Select
                            value={formData.personType}
                            onChange={handleInput("personType")}
                          >
                            <option value="owner">Proprietario</option>
                            <option value="tenant">Inquilino</option>
                          </Select>
                        </Td>
                        <Td>
                          <Input
                            list={`person-list-${index}-${vi}`}
                            value={formData.nome || ""}
                            onChange={handleInput("nome")}
                          />
                          <datalist id={`person-list-${index}-${vi}`}>
                            {filteredPersons.map((p) => (
                              <option key={p.id} value={p.name} />
                            ))}
                          </datalist>
                        </Td>
                        <Td>
                          <Input
                            list={`person-id-list-${index}-${vi}`}
                            value={formData.person_id || ""}
                            onChange={handleInput("person_id")}
                          />
                          <datalist id={`person-id-list-${index}-${vi}`}>
                            {filteredPersons.map((p) => (
                              <option key={p.id} value={p.id} />
                            ))}
                          </datalist>
                        </Td>
                        <Td>
                          <Select
                            value={formData.gestione || "VO"}
                            onChange={handleInput("gestione")}
                          >
                            <option value="VO">VO</option>
                            <option value="VS">VS</option>
                            <option value="RISC">RISC</option>
                          </Select>
                        </Td>
                        <Td>
                          <input
                            type="number"
                            value={formData.num_prev ?? ''}
                            onChange={handleInput('num_prev')}
                            min={1}
                            required
                          />
                        </Td>
                        <Td>
                          <Input
                            type="number"
                            value={formData.imp_tot || ""}
                            onChange={handleInput("imp_tot")}
                          />
                        </Td>
                        <Td>
                          <button onClick={() => handleSave(vi)}>Salva</button>
                          <button onClick={cancel}>Cancel</button>
                        </Td>
                      </tr>
                    ) : (
                      <tr key={vi}>
                        <Td>{v.ui_id}</Td>
                        <Td>{v.person_role === "tenant" ? "Inquilino" :
      v.person_role === "owner"  ? "Proprietario" : "—"}</Td>

                        <Td>{v.nome}</Td>
                        <Td>{v.person_id}</Td>
                        <Td>{v.gestione}</Td>
                        <Td>{v.num_prev}</Td>
                        <Td>{v.imp_tot.toFixed(2)}</Td>
                        <Td>
                          <button onClick={() => startEdit(v, vi)}>Modifica</button>
                          <button onClick={() => handleDelete(vi)}>
                            Elimina
                          </button>
                        </Td>
                      </tr>
                    )
                  )}

                  {addMode ? (
                    <tr>
                      <Td>
                        <Input
                          list={`ui-list-add-${index}`}
                          value={formData.ui_id || ""}
                          onChange={handleInput("ui_id")}
                        />
                        <datalist id={`ui-list-add-${index}`}>
                          {filteredUnits.map((u) => (
                            <option key={u.cod_ui} value={u.cod_ui}>
                              {u.des_ui}
                            </option>
                          ))}
                        </datalist>
                      </Td>
                      <Td>
                        <Select
                          value={formData.personType}
                          onChange={handleInput("personType")}
                        >
                          <option value="owner">Proprietario</option>
                          <option value="tenant">Inquilino</option>
                        </Select>
                      </Td>
                      <Td>
                        <Input
                          list={`person-list-add-${index}`}
                          value={formData.nome || ""}
                          onChange={handleInput("nome")}
                        />
                        <datalist id={`person-list-add-${index}`}>
                          {filteredPersons.map((p) => (
                            <option key={p.id} value={p.name} />
                          ))}
                        </datalist>
                      </Td>
                      <Td>
                        <Input
                          list={`person-id-list-add-${index}`}
                          value={formData.person_id || ""}
                          onChange={handleInput("person_id")}
                        />
                        <datalist id={`person-id-list-add-${index}`}>
                          {filteredPersons.map((p) => (
                            <option key={p.id} value={p.id} />
                          ))}
                        </datalist>
                      </Td>
                      <Td>
                        <Select
                          value={formData.gestione || "VO"}
                          onChange={handleInput("gestione")}
                        >
                          <option value="VO">VO</option>
                          <option value="VS">VS</option>
                          <option value="RISC">RISC</option>
                        </Select>
                      </Td>
                      <Td>
                        <input
                          type="number"
                          value={formData.num_prev ?? ''}
                          onChange={handleInput('num_prev')}
                          min={1}
                          required
                        />
                      </Td>
                      <Td>
                        <Input
                          type="number"
                          value={formData.imp_tot || ""}
                          onChange={handleInput("imp_tot")}
                        />
                      </Td>
                      <Td>
                        <button onClick={() => handleSave()}>Aggiungi</button>
                        <button onClick={cancel}>Cancella</button>
                      </Td>
                    </tr>
                  ) : (
                    <tr>
                      <Td colSpan={7} style={{ textAlign: "center" }}>
                        <button onClick={startAdd}>Aggiungi Versamento</button>
                      </Td>
                    </tr>
                  )}
                </tbody>
              </InnerTable>
            </ScrollWrapper>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default React.memo(TransactionRow);
